import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useAuth } from '../contexts/AuthContext'
import type { TenantThemeConfig } from '../types/theme'
import { InfoModal } from './InfoModal'
import { getTenantIdFromConfig } from '../utils/tenantUtils'
import { getThemeAssetUrl } from '../utils/themeUtils'

// Extend JSX IntrinsicElements to include marquee
declare global {
  namespace JSX {
    interface IntrinsicElements {
      marquee: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        direction?: string
        onMouseOver?: (event: React.MouseEvent<HTMLElement>) => void
        onMouseOut?: (event: React.MouseEvent<HTMLElement>) => void
      }
    }
  }
}

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ClassicLayoutProps {
  children: React.ReactNode
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const ClassicLayout: React.FC<ClassicLayoutProps> = ({ children, themeConfig, assetConfig }) => {
  // Navigation hook
  const navigate = useNavigate()
  
  // Auth context
  const { login, isAuthenticated, user } = useAuth()
  
  // Get tenant ID using utility function
  const getTenantId = (): string => {
    return getTenantIdFromConfig(themeConfig, user)
  }
  
  // Use theme-specific assets path based on themeConfig
  const getAssetUrl = (path: string) => {
    return getThemeAssetUrl(assetConfig.CDN_BASE_URL, themeConfig.themeId, path)
  }
  
  // Extract configuration from themeConfig
  const { branding } = themeConfig.theme
  const { marqueeText, rulesAndRegulations, paymentRates } = themeConfig.settings
  
  // Debug: log data to console
  if (process.env.NODE_ENV === 'development') {
    // console.log('🔍 ClassicLayout - themeConfig.settings:', themeConfig.settings);
    // console.log('🔍 ClassicLayout - rulesAndRegulations:', rulesAndRegulations);
    // console.log('🔍 ClassicLayout - paymentRates:', paymentRates);
  }
  
  // Modal states - ensure only one modal is open at a time
  const [activeModal, setActiveModal] = useState<'rules' | 'payment' | null>(null)
  
  // Login form states
  const [loginForm, setLoginForm] = useState({
    username: localStorage.getItem('savedUsername'), // Default for testing
    password: '', // Default for testing
    rememberMe: !!localStorage.getItem('savedUsername') // true ถ้ามี saved username
  })
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  
  // Check for concurrent login on component mount
  useEffect(() => {
    const checkConcurrentLogin = () => {
      // Check if there's a stored flag indicating concurrent login
      const concurrentLoginFlag = sessionStorage.getItem('concurrentLoginDetected')
      if (concurrentLoginFlag) {
        sessionStorage.removeItem('concurrentLoginDetected')
        
        // Show countdown alert
        let countdown = 5
        Swal.fire({
          icon: 'warning',
          title: 'มีการเข้าสู่ระบบจากอุปกรณ์อื่น',
          html: `
            <div class="text-center">
              <p class="mb-2">บัญชีของคุณได้เข้าสู่ระบบจากอุปกรณ์อื่นแล้ว</p>
              <p class="mb-0">กำลังกลับไปหน้าเดิม ใน <span id="logoutCountdown" class="fw-bold text-danger">${countdown}</span> วินาที</p>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          timer: 5000,
          timerProgressBar: true,
          willClose: () => {
            // Force logout current session
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('user')
            sessionStorage.clear()
            // Reload page to reset state
            window.location.reload()
          }
        })

        // Update countdown
        const countdownInterval = setInterval(() => {
          countdown--
          const countdownElement = document.getElementById('logoutCountdown')
          if (countdownElement && countdown > 0) {
            countdownElement.textContent = countdown.toString()
          }
          if (countdown <= 0) {
            clearInterval(countdownInterval)
          }
        }, 1000)
      }
    }

    checkConcurrentLogin()
  }, [])

  // Clear stale auth data on component mount
  useEffect(() => {
    // Check if user is not authenticated but still has tokens
    if (!isAuthenticated) {
      const hasTokens = localStorage.getItem('accessToken') || localStorage.getItem('refreshToken')
      if (hasTokens) {
        console.log('🧹 Clearing stale auth data')
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        localStorage.removeItem('deviceId')
      }
    }
  }, [isAuthenticated])
  
  // Error message mapping
  const getThaiErrorMessage = (errorMessage: string): string => {
          const errorMappings: Record<string, string> = {
        'Account is not active': 'บัญชีของคุณถูกปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ',
        'Account has been disabled by administrator': 'บัญชีของคุณถูกปิดใช้งานโดยผู้ดูแลระบบ',
        'Account is temporarily locked due to too many failed login attempts': 'บัญชีของคุณถูกล็อคชั่วคราวเนื่องจากเข้าสู่ระบบผิดหลายครั้ง กรุณารอสักครู่แล้วลองใหม่',
        'Invalid credentials': 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
        'User not found': 'ไม่พบผู้ใช้ในระบบ',
        'Account is locked': 'บัญชีของคุณถูกล็อค กรุณาติดต่อผู้ดูแลระบบ',
        'Too many login attempts': 'พยายามเข้าสู่ระบบมากเกินไป กรุณารอสักครู่แล้วลองใหม่',
        'Login failed': 'เข้าสู่ระบบไม่สำเร็จ กรุณาตรวจสอบชื่อผู้ใช้และรหัสผ่าน',
        'Network error': 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง',
        'เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง': 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง'
      }
    
    return errorMappings[errorMessage] || errorMessage || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
  }

  // Handle login form submission
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form
    if (!loginForm.username?.trim() || !loginForm.password?.trim()) {
      await Swal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณากรอกชื่อผู้ใช้หรือเบอร์โทรศัพท์และรหัสผ่าน',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#ffc107'
      })
      return
    }
    
    setIsLoggingIn(true)
    
    try {
      const tenantId = getTenantId()
      const loginResult = await login(loginForm.username?.trim() || '', loginForm.password, loginForm.rememberMe, tenantId)
      
      if (loginResult.success) {
        // Handle remember me functionality
        if (loginForm.rememberMe) {
          localStorage.setItem('savedUsername', loginForm.username)
        } else {
          localStorage.removeItem('savedUsername')
        }
        
        // Navigate ทันทีเพื่อให้ URL และ UI sync กัน
        navigate('/dashboard')
        
        // แสดง success alert พร้อม countdown กลางจอ
        let countDown = 3
        Swal.fire({
          icon: 'success',
          title: 'เข้าสู่ระบบสำเร็จ!',
          html: `
            <div class="text-center">
              <p class="mb-2">ยินดีต้อนรับ <strong>${loginForm.username}</strong></p>
              <p class="mb-0">กำลังเข้าสู่ระบบ ใน <span id="countdown" class="fw-bold text-primary">${countDown}</span> วินาที</p>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          timer: 3000,
          timerProgressBar: true
        })

        // อัพเดท countdown ทุกวินาที
        const countdownInterval = setInterval(() => {
          countDown--
          const countdownElement = document.getElementById('countdown')
          if (countdownElement && countDown > 0) {
            countdownElement.textContent = countDown.toString()
          }
          if (countDown <= 0) {
            clearInterval(countdownInterval)
          }
        }, 1000)
      } else {
        let errorMessage = 'ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่อีกครั้ง'
        
        // Check for concurrent login error
        if (loginResult.errorMessage === 'CONCURRENT_LOGIN_BLOCKED' || 
            loginResult.errorMessage?.includes('CONCURRENT_LOGIN_BLOCKED') || 
            loginResult.errorMessage?.includes('already logged in')) {
          
          // Check if user actually has active session by trying to get user info
          try {
            const { default: authService } = await import('../services/authService')
            const currentToken = authService.getAccessToken()
            
            // If no token, don't show concurrent login message
            if (!currentToken) {
              console.log('🔄 No active token found, skipping concurrent login handling')
              errorMessage = 'กรุณาลองเข้าสู่ระบบใหม่อีกครั้ง'
            } else {
              // Show loading message
              Swal.fire({
                title: 'กำลังออกจากระบบจากอุปกรณ์อื่น...',
                text: 'กรุณารอสักครู่',
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                  Swal.showLoading()
                }
              })
              
              // Get tenant ID dynamically
              const tenantId = getTenantId()
              await authService.forceLogoutFromAllDevices(tenantId, loginForm.username)
              
              // Wait a moment for logout to complete
              await new Promise(resolve => setTimeout(resolve, 1000))
              
              // Close loading and show success
              Swal.fire({
                icon: 'success',
                title: 'ออกจากระบบสำเร็จ',
                text: 'กำลังเข้าสู่ระบบใหม่...',
                timer: 1500,
                showConfirmButton: false
              })
              
              // Retry login automatically
              setTimeout(async () => {
                const retryResult = await login(loginForm.username?.trim() || '', loginForm.password, loginForm.rememberMe, tenantId)
                if (retryResult.success) {
                  // Handle remember me functionality
                  if (loginForm.rememberMe) {
                    localStorage.setItem('savedUsername', loginForm.username || '')
                  } else {
                    localStorage.removeItem('savedUsername')
                  }
                  
                  navigate('/dashboard')
                  Swal.fire({
                    icon: 'success',
                    title: 'เข้าสู่ระบบสำเร็จ!',
                    text: `ยินดีต้อนรับ ${loginForm.username}`,
                    timer: 2000,
                    showConfirmButton: false
                  })
                }
              }, 1500)
              
              return // Exit early, no error message needed
            }
          } catch (forceLogoutError) {
            console.error('❌ Auto force logout error:', forceLogoutError)
            Swal.close()
            errorMessage = 'เกิดข้อผิดพลาดในการออกจากระบบ กรุณาลองใหม่อีกครั้ง'
          }
        }
        
        // แสดง error alert ด้วยข้อความที่ได้จาก API
        const thaiErrorMessage = getThaiErrorMessage(errorMessage || loginResult.errorMessage || 'Login failed')
        await Swal.fire({
          icon: 'error',
          title: 'เข้าสู่ระบบไม่สำเร็จ',
          text: thaiErrorMessage,
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#dc3545'
        })
      }
    } catch (error) {
      // แสดง error alert สำหรับ network error หรือ unexpected error
      const errorMessage = error instanceof Error ? error.message : 'Network error'
      const thaiErrorMessage = getThaiErrorMessage(errorMessage)
      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: thaiErrorMessage,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setIsLoggingIn(false)
    }
  }
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setLoginForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  return (
    <>
      {/* Marquee Section - ตรงกับต้นฉบับ HuayAsia */}
      <section className="content">
        <div className="container">
          <div className="wrap-noti bgc-4 bdr-24 px-2 my-2">
            <marquee
              direction="left"
              onMouseOver={(e: React.MouseEvent<HTMLElement>) => (e.target as any).stop()}
              onMouseOut={(e: React.MouseEvent<HTMLElement>) => (e.target as any).start()}
            >
              {marqueeText || 'ยินดีต้อนรับทุกท่านเข้าสู่เว็บ HuayToday.com เว็บหวยออนไลน์ที่มาแรงที่สุดในตอนนี้ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย'}
            </marquee>
          </div>
        </div>
      </section>

      <a id="button" className=""></a>
      <div className="bg-fixed"></div>

      {/* Header Section - ตรงตามต้นฉบับ HuayAsia */}
      <header className="header">
        <div className="container">
          <div className="wrap bgc-15 bdr-10 bx-sd">
            <div className="bgg-1 bdr-10 wrap p-2 justify-content-between w-100">
              <a href="index.html" className="logo">
                <img src={branding.logoUrl} alt="" onError={(e) => {
                  (e.target as HTMLImageElement).src = getAssetUrl('/img/logo.png')
                }} />
              </a>

              <div className="box-login">
                <form onSubmit={handleLogin} className="wrap gap-x-1">
                  {/* Error Message */}

                  
                  <div className="form-inp bdr-5">
                    <div className="icon">
                      <i className="fas fa-user"></i>
                    </div>
                    <input 
                      type="text" 
                      name="username"
                      value={loginForm.username || ''}
                      onChange={handleInputChange}
                      placeholder="ชื่อผู้ใช้ หรือ เบอร์โทรศัพท์"
                      disabled={isLoggingIn}
                      autoComplete="username"
                    />
                  </div>

                  <div className="form-inp bdr-5">
                    <div className="icon">
                      <i className="fas fa-key"></i>
                    </div>
                    <input 
                      type="password" 
                      name="password"
                      value={loginForm.password}
                      onChange={handleInputChange}
                      placeholder="รหัสผ่าน" 
                      disabled={isLoggingIn}
                      autoComplete="current-password"
                    />
                  </div>

                  <div className="wrap_2 px-0 px-lg-1">
                    <div className="checkboxes">
                      <div className="checkboxes__row">
                        <div className="checkboxes__item">
                          <label className="checkbox style-a">
                            <input
                              type="checkbox"
                              name="rememberMe"
                              checked={loginForm.rememberMe}
                              onChange={handleInputChange}
                              disabled={isLoggingIn}
                            />
                            <div className="checkbox__checkmark"></div>
                            <div className="checkbox__body c-white">
                              จำฉันไว้ในระบบ
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <a 
                      href="#" 
                      className="c-white"
                      onClick={(e) => e.preventDefault()}
                    >
                      <i className="fas fa-key"></i> ลืมรหัสผ่าน
                    </a>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoggingIn}
                    className="btn_main c-red wrap text-nowrap justify-content-center t-medium bgg-2 bdr-5"
                    style={{ 
                      opacity: isLoggingIn ? 0.7 : 1,
                      cursor: isLoggingIn ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isLoggingIn ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        กำลังเข้าสู่ระบบ...
                      </>
                    ) : (
                      'เข้าสู่ระบบ'
                    )}
                  </button>
                  
                  {/* Register Link */}
                  <div className="text-center mt-2">
                    <Link 
                      to="/register" 
                      className="c-white text-decoration-none"
                      style={{ fontSize: '14px' }}
                    >
                      <i className="fas fa-user-plus me-1"></i>
                      สมัครสมาชิก
                    </Link>
                  </div>
                </form>
              </div>
            </div>

            <div
              className="wrap w-100 wrap-login"
              style={{borderRadius: '0 0 10px 10px'}}
            >
              <div className="bgi-1 bdr-10 p-2 justify-content-between w-100">
                <div className="row mx--1">
                  <div className="col-12 col-lg-4 px-1">
                    <div className="d-flex gap-2">
                      <button
                        onClick={() => setActiveModal('payment')}
                        className="w-50 my-1 py-1 c-white wrap text-nowrap justify-content-center t-regular bgg-5 bdr-5 border-0"
                      >
                        <i className="fas fa-dollar-sign me-1"></i> อัตราการจ่าย
                      </button>
                      <button
                        onClick={() => setActiveModal('rules')}
                        className="w-50 my-1 py-1 c-white wrap text-nowrap justify-content-center t-regular bgg-5 bdr-5 border-0"
                      >
                        <i className="fas fa-tasks me-1"></i> กฏกติกาข้อบังคับ
                      </button>
                    </div>
                  </div>

                  <div className="col-12 offset-lg-4 col-lg-4 px-1">
                    <div className="d-flex gap-2">
                      <Link
                        to="/register"
                        className="w-50 my-1 py-1 c-white wrap text-nowrap justify-content-center t-regular bgg-1 bdr-5"
                      >
                        <span className="blinkblink">
                          <i className="fas fa-user-check me-1"></i>
                          สมัครสมาชิก
                        </span>
                      </Link>
                      <Link
                        to="/contact"
                        className="w-50 my-1 py-1 c-white wrap text-nowrap justify-content-center t-regular bgg-4 bdr-5"
                      >
                        <i className="fab fa-line me-1"></i> ติดต่อเรา
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </header>

      {/* Main Content */}
      {children}

      {/* Footer */}
      <footer className="footer c-red text-center py-2 mb-4 animateMe" data-animation="fadeInUp">
        <small>{branding.footerText || `Copyright © 2024 ${branding.siteName}. All rights reserved.`}</small>
      </footer>

      {/* Modals */}
      {/* Rules Modal */}
      {rulesAndRegulations && (
        <InfoModal
          isOpen={activeModal === 'rules'}
          onClose={() => setActiveModal(null)}
          title="กฎกติกาข้อบังคับ"
          content={rulesAndRegulations}
        />
      )}

      {/* Payment Rates Modal */}
      {paymentRates && (
        <InfoModal
          isOpen={activeModal === 'payment'}
          onClose={() => setActiveModal(null)}
          title="อัตราการจ่าย"
          content={paymentRates}
        />
      )}
    </>
  )
}

export default ClassicLayout
